import React, { useState } from "react";
import { Modal, Input, Button, InputNumber, Divider, message } from "antd";
import {
  TagOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ArrowRightOutlined,
  GiftOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { MdPayment } from "react-icons/md";
import drEasyApi from "../../../Api/Customapi";

const CouponApplyModal = ({ visible, invoiceId, invoiceTotal = 0, onDone, isMobile }) => {
  const [code, setCode]             = useState("");
  const [validating, setValidating] = useState(false);
  const [applying, setApplying]     = useState(false);
  const [validation, setValidation] = useState(null);   // null | response
  const [applied, setApplied]       = useState(false);  // coupon locked in
  const [paidAmount, setPaidAmount] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const effectiveTotal =
    applied && validation?.invoiceTotalAfter != null
      ? validation.invoiceTotalAfter
      : invoiceTotal;

  const remaining = Math.max(0, effectiveTotal - paidAmount);

  const isChecked  = validation !== null;
  const isValid    = validation?.isValid === true;
  const isInvalid  = validation !== null && !validation.isValid;

  React.useEffect(() => {
    if (visible) {
      setCode("");
      setValidation(null);
      setApplied(false);
      setPaidAmount(0);
    }
  }, [visible]);

  // ── clear coupon ─────────────────────────────────────────────────────────
  const clearCoupon = () => {
    setCode("");
    setValidation(null);
    setApplied(false);
  };

  // ── validate ─────────────────────────────────────────────────────────────
  const handleValidate = async () => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) { message.warning("Please enter a coupon code"); return; }
    setValidating(true);
    setValidation(null);
    setApplied(false);
    try {
      const res = await drEasyApi.get("/api/Coupons/Validate", {
        params: { CouponCode: trimmed, InvoiceId: invoiceId },
      });
      setValidation(res.data);
      if (!res.data.isValid) message.error(res.data.message || "Invalid coupon");
    } catch {
      message.error("Failed to validate coupon");
    } finally {
      setValidating(false);
    }
  };

  // ── apply ─────────────────────────────────────────────────────────────────
  const handleApply = async () => {
    if (!isValid) return;
    setApplying(true);
    try {
      await drEasyApi.post("/api/Coupons/Apply", {
        invoiceId,
        couponCode: validation.code,
      });
      setApplied(true);
      message.success(`Coupon applied — saving EGP ${Number(validation.estimatedDiscount).toFixed(2)}!`);
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to apply coupon");
    } finally {
      setApplying(false);
    }
  };

  // ── confirm (pay) ─────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await drEasyApi.post("/api/Invoice/Pay", {
        invoiceId,
        paidAmount: paidAmount || 0,
      });
      // pass actual paid amount + coupon discount back so the print snapshot is correct
      onDone?.({
        paidAmount: paidAmount || 0,
        couponDiscount: applied ? (validation?.estimatedDiscount ?? 0) : 0,
        totalAfterCoupon: effectiveTotal,
      });
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to record payment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={visible}
      footer={null}
      width={isMobile ? "92vw" : 460}
      centered
      closable={false}
      maskClosable={false}
      title={
        <div className="flex items-center gap-2">
          <MdPayment className="text-primary text-xl" />
          <span className="font-semibold text-gray-800">Payment</span>
        </div>
      }
    >

      {/* ── Invoice total row ── */}
      <div className="flex justify-between items-center py-2">
        <span className="text-sm text-gray-500">Invoice Total</span>
        <span className="font-bold text-gray-800 text-base">
          EGP {Number(invoiceTotal).toFixed(2)}
        </span>
      </div>

      {/* ══ COUPON SECTION ═══════════════════════════════════════════════════ */}
      <Divider className="!my-3" />

      <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
        <GiftOutlined className="text-primary" />
        Coupon?
        {/* <span className="text-gray-400 font-normal ml-1">(optional)</span> */}
      </p>

      {/* ── Input + two-button row ── */}
      {!applied ? (
        <div className="flex gap-2 mb-2">
          <Input
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              // reset validation when user edits
              if (validation) { setValidation(null); setApplied(false); }
            }}
            onKeyDown={(e) => e.key === "Enter" && !isValid && handleValidate()}
            placeholder="Enter promo code"
            prefix={<TagOutlined className="text-gray-400" />}
            style={{ fontFamily: "monospace", letterSpacing: "0.06em" }}
            className={
              isValid   ? "!border-green-400 focus:!border-green-500" :
              isInvalid ? "!border-red-400   focus:!border-red-500"   : ""
            }
            disabled={validating || applying || submitting}
            suffix={
              code ? (
                <CloseOutlined
                  className="text-gray-400 cursor-pointer hover:text-gray-600 text-xs"
                  onClick={clearCoupon}
                />
              ) : null
            }
          />

          {/* Validate button — shown until code is confirmed valid */}
          {!isValid && (
            <Button
              onClick={handleValidate}
              loading={validating}
              disabled={!code.trim() || applying || submitting}
              className="shrink-0"
              style={{ backgroundColor: "#298d9f", borderColor: "#298d9f", color: "#fff" }}
            >
              Apply
            </Button>
          )}

          {/* Apply button — only shown after successful validation */}
          {isValid && !applied && (
            <Button
              onClick={handleApply}
              loading={applying}
              disabled={submitting}
              className="shrink-0"
              style={{ backgroundColor: "#16a34a", borderColor: "#16a34a", color: "#fff" }}
            >
              Use Coupon
            </Button>
          )}
        </div>
      ) : (
        /* ── Applied state chip ── */
        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-2">
          <div className="flex items-center gap-2">
            <CheckCircleFilled className="text-green-500" />
            <span className="font-mono font-semibold text-green-700 tracking-wider">
              {validation.code}
            </span>
            <span className="text-green-600 text-sm">
              — saving EGP {Number(validation.estimatedDiscount).toFixed(2)}
            </span>
          </div>
      
        </div>
      )}

      {/* ── Inline feedback (invalid only; valid transitions to Apply btn) ── */}
      {isInvalid && (
        <p className="text-red-500 text-xs flex items-center gap-1 mb-1">
          <CloseCircleFilled />
          {validation.message || "Invalid coupon code"}
        </p>
      )}

      {/* ── Preview row (valid but not yet applied) ── */}
      {isValid && !applied && (
        <p className="text-green-600 text-xs flex items-center gap-1 mb-1">
          <CheckCircleFilled />
          {validation.type === 1
            ? `${validation.value}% off`
            : `EGP ${validation.value} off`}
          {" "}— total becomes{" "}
          <strong>EGP {Number(validation.invoiceTotalAfter).toFixed(2)}</strong>
        </p>
      )}

      {/* ── Updated total after coupon applied ── */}
      {applied && (
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm text-gray-500">Total after coupon</span>
          <span className="font-bold text-primary text-base">
            EGP {Number(effectiveTotal).toFixed(2)}
          </span>
        </div>
      )}

      {/* ══ PAYMENT SECTION ══════════════════════════════════════════════════ */}
      <Divider className="!my-3" />

      <p className="text-sm font-medium text-gray-700 mb-1.5">Amount Paid Now</p>
      <InputNumber
        className="w-full"
        size="large"
        min={0}
        step={1}
        precision={2}
        value={paidAmount}
        onChange={(val) => setPaidAmount(val ?? 0)}
        prefix="EGP"
        placeholder="0.00"
        disabled={submitting}
      />

      {/* ── Remaining balance live pill ── */}
      <div className={`flex justify-between items-center mt-3 px-3 py-2.5 rounded-lg transition-colors
        ${remaining === 0
          ? "bg-emerald-50 border border-emerald-200"
          : "bg-orange-50 border border-orange-200"}`}
      >
        <span className={`text-sm font-semibold ${remaining === 0 ? "text-emerald-700" : "text-orange-700"}`}>
          {remaining === 0 ? "✓ Fully Paid" : "Remaining"}
        </span>
        <span className={`font-bold text-lg tabular-nums ${remaining === 0 ? "text-emerald-600" : "text-orange-600"}`}>
          EGP {remaining.toFixed(2)}
        </span>
      </div>

      {/* ── Actions ── */}
      <div className="flex gap-3 mt-4">
        <Button
          block size="large"
          onClick={onDone}
          disabled={submitting}
          className="border-gray-300 text-gray-600"
        >
          Skip
        </Button>
        <Button
          block size="large" type="primary"
          loading={submitting}
          onClick={handleConfirm}
          icon={<ArrowRightOutlined />}
          style={{ backgroundColor: "#16a34a", borderColor: "#16a34a" }}
        >
          Confirm & Print
        </Button>
      </div>

    </Modal>
  );
};

export default CouponApplyModal;